/**
 * WebRTC DataChannel direct path — SPEC §10–11 / PLAN Phase D.
 * TURN servers come from signed bootstrap; application E2EE remains mandatory
 * because TURN observes IP/timing metadata.
 */

import type { BootstrapDocument } from "@veto/protocol";
import { DEFAULT_BOOTSTRAP } from "./bootstrap";

export interface TurnServer {
  urls: string;
  username?: string;
  credential?: string;
}

export function iceServersFromBootstrap(
  bootstrap: BootstrapDocument = DEFAULT_BOOTSTRAP,
  credentials?: { username: string; credential: string },
): RTCIceServer[] {
  return bootstrap.turn.map((t) => ({
    urls: t.url.startsWith("turn:") || t.url.startsWith("turns:") ? t.url : `turn:${t.url}`,
    username: credentials?.username ?? "veto-dev",
    credential: credentials?.credential ?? "veto-dev",
  }));
}

export type DirectPeerState = "idle" | "connecting" | "open" | "closed" | "failed";

/** Which side of the handshake this link is (glare resolution needs it). */
export type DirectPeerRole = "offer" | "answer";

export interface DirectPeerHandlers {
  onMessage?: (data: string) => void;
  onState?: (state: DirectPeerState) => void;
}

/** A link that hasn't opened by then is given up (an unanswered offer, a lost answer, no route). */
export const DIRECT_CONNECT_TIMEOUT_MS = 20_000;

/** Frames skip the relays' limits, so the link applies its own: size and rate per second. */
export const DIRECT_MAX_FRAME_CHARS = 256 * 1024;
export const DIRECT_MAX_FRAMES_PER_SECOND = 40;

/**
 * Minimal authenticated DataChannel wrapper. Offers/answers travel inside the E2EE session
 * (content type 8), which also authenticates the DTLS fingerprints.
 * "closed" and "failed" are final: the peer connection is torn down and the link never reopens.
 */
export class DirectPeerLink {
  private pc: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private windowStart = 0;
  private windowFrames = 0;
  state: DirectPeerState = "idle";
  role: DirectPeerRole | null = null;
  /** When the handshake started (ms): glare resolution only applies to a fresh offer. */
  startedAt = 0;

  constructor(
    private readonly iceServers: RTCIceServer[],
    private readonly handlers: DirectPeerHandlers = {},
    private readonly connectTimeoutMs = DIRECT_CONNECT_TIMEOUT_MS,
  ) {}

  private setState(s: DirectPeerState) {
    if (this.state === s || this.state === "closed" || this.state === "failed") return;
    this.state = s;
    if (s === "open") this.clearTimer();
    this.handlers.onState?.(s);
  }

  private clearTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private start(role: DirectPeerRole): RTCPeerConnection {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    this.pc = pc;
    this.role = role;
    this.startedAt = Date.now();
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" || pc.connectionState === "closed") this.shutdown("failed");
    };
    this.setState("connecting");
    this.timer = setTimeout(() => {
      if (this.state !== "open") this.shutdown("failed");
    }, this.connectTimeoutMs);
    return pc;
  }

  /** Caller creates offer side. */
  async createOfferPeer(label = "veto-mesh"): Promise<RTCSessionDescriptionInit> {
    const pc = this.start("offer");
    this.channel = pc.createDataChannel(label, { ordered: true });
    this.bindChannel(this.channel);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGathering(pc);
    if (!pc.localDescription || this.pc !== pc) throw new Error("direct link closed");
    return pc.localDescription.toJSON();
  }

  /** Callee accepts remote offer. */
  async acceptOffer(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    const pc = this.start("answer");
    pc.ondatachannel = (ev) => {
      if (this.pc !== pc) return;
      this.channel = ev.channel;
      this.bindChannel(ev.channel);
    };
    await pc.setRemoteDescription(offer);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await waitForIceGathering(pc);
    if (!pc.localDescription || this.pc !== pc) throw new Error("direct link closed");
    return pc.localDescription.toJSON();
  }

  /** Applies the answer to our own pending offer; false (ignored) for anything else. */
  async applyAnswer(answer: RTCSessionDescriptionInit): Promise<boolean> {
    const pc = this.pc;
    if (!pc || this.role !== "offer" || pc.signalingState !== "have-local-offer") return false;
    await pc.setRemoteDescription(answer);
    return true;
  }

  send(payload: string): void {
    if (!this.channel || this.channel.readyState !== "open") {
      throw new Error("datachannel not open");
    }
    // Opaque envelopes only — never plaintext (SPEC §11.4)
    this.channel.send(payload);
  }

  close(): void {
    this.shutdown("closed");
  }

  private withinRate(now = Date.now()): boolean {
    if (now - this.windowStart >= 1000) {
      this.windowStart = now;
      this.windowFrames = 0;
    }
    return ++this.windowFrames <= DIRECT_MAX_FRAMES_PER_SECOND;
  }

  private shutdown(final: "closed" | "failed") {
    this.clearTimer();
    const { channel, pc } = this;
    this.channel = null;
    this.pc = null;
    if (channel) {
      channel.onopen = channel.onclose = channel.onerror = channel.onmessage = null;
      try {
        channel.close();
      } catch {
        /* already closed */
      }
    }
    if (pc) {
      pc.onconnectionstatechange = null;
      pc.ondatachannel = null;
      try {
        pc.close();
      } catch {
        /* already closed */
      }
    }
    this.setState(final);
  }

  private bindChannel(ch: RTCDataChannel) {
    ch.binaryType = "arraybuffer";
    ch.onopen = () => this.setState("open");
    // The channel is the link: when it goes, the peer connection goes too.
    ch.onclose = () => this.shutdown("closed");
    ch.onerror = () => this.shutdown("failed");
    ch.onmessage = (ev) => {
      const size = typeof ev.data === "string" ? ev.data.length : (ev.data as ArrayBuffer).byteLength;
      if (!(size > 0) || size > DIRECT_MAX_FRAME_CHARS || !this.withinRate()) return;
      const data = typeof ev.data === "string" ? ev.data : new TextDecoder().decode(ev.data);
      this.handlers.onMessage?.(data);
    };
  }
}

function waitForIceGathering(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const check = () => {
      if (pc.iceGatheringState === "complete") {
        pc.removeEventListener("icegatheringstatechange", check);
        resolve();
      }
    };
    pc.addEventListener("icegatheringstatechange", check);
    // Bound wait — host candidates may never complete in restricted CI
    setTimeout(() => resolve(), 1500);
  });
}

export function describeDirectPath(bootstrap: BootstrapDocument = DEFAULT_BOOTSTRAP): string {
  return `WebRTC DataChannel with ${bootstrap.turn.length} TURN URLs from bootstrap (E2EE still required)`;
}
